import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Public page reached via the quote email's "Accept" link.
 * The URL token IS the auth — no login required. Idempotent: if the quote
 * was already accepted, the API returns 404 and we show a friendly message.
 */
export default function QuoteAccept() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // "loading" | "ok" | "err"
  const [orderNumber, setOrderNumber] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.post(`/quotes/accept/${token}`);
        if (cancelled) return;
        setOrderNumber(r.data.order_number);
        setState("ok");
      } catch (e) {
        if (cancelled) return;
        setErrMsg(formatErr(e.response?.data?.detail) || e.message);
        setState("err");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-secondary/30 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-background border border-border rounded-sm p-8 shadow-sm text-center" data-testid="quote-accept-card">
        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-4" />
            <p className="overline">Processing</p>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-2">Accepting your quote…</h1>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-success mb-4" />
            <p className="overline text-success">Accepted</p>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2" data-testid="quote-accept-success">
              Order {orderNumber} placed
            </h1>
            <p className="text-sm text-muted-foreground mt-3">
              Thanks for accepting. You&apos;ll receive a separate email with payment instructions shortly.
            </p>
            <Link to="/portal" className="inline-block mt-6 bg-foreground text-background rounded-sm h-11 px-6 leading-[2.75rem] font-semibold hover:bg-foreground/90 transition-colors" data-testid="quote-accept-portal-link">
              Open your portal
            </Link>
          </>
        )}
        {state === "err" && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <p className="overline text-destructive">Couldn&apos;t accept</p>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-2">Link no longer valid</h1>
            <p className="text-sm text-muted-foreground mt-3" data-testid="quote-accept-error">{errMsg}</p>
            <p className="text-xs text-muted-foreground mt-3">
              This quote may have already been accepted, declined, or expired. Open your portal to see your latest orders.
            </p>
            <Link to="/portal" className="inline-block mt-6 bg-foreground text-background rounded-sm h-11 px-6 leading-[2.75rem] font-semibold hover:bg-foreground/90 transition-colors">
              Open portal
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
